import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, timeout } from 'rxjs/operators';
import { of } from 'rxjs';

import * as URLs from '../../../global.urls';
import { UserService } from '../../../services/user.service';
import { GoogleIdentityService } from '../../../services/google-identity.service';
import { InactivityTimeoutService } from '../../../services/inactivity.timeout.service';
import { TrendingHomePostVM } from '../trending-post-card/trending-post-card.component';

@Component({
    selector: 'app-public-home',
    templateUrl: './public-home.component.html',
    styleUrls: ['./public-home.component.scss']
})
export class PublicHomeComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('googleBtnHero') googleBtnHero: ElementRef<HTMLElement>;
    @ViewChild('googleBtnModal') googleBtnModal: ElementRef<HTMLElement>;

    returnUrl: string;
    email = '';
    password = '';
    errorMessage = '';

    showAuthModal = false;
    authModalTab: 'signIn' | 'create' = 'create';

    createEmail = '';
    createUsername = '';
    createDisplayName = '';
    createPassword = '';
    createShowPassword = false;
    createErrors: Record<string, string> = {};
    createBusy = false;

    googleUsernameStep = false;
    pendingGoogleToken: string;
    googleSuggestUsername = '';
    googleSuggestProfile = '';
    googlePrefillEmail = '';

    trending: TrendingHomePostVM[] = [];
    trendingLoading = true;
    trendingError: string;

    loginBusy = false;
    googleBusy = false;

    /** True if assets/public/images/mainlogo_lg.png failed to load (run dotnet build on AspNetCore first to copy files). */
    logoLoadFailed = false;

    readonly copyrightYear = new Date().getFullYear();

    constructor(
        private readonly http: HttpClient,
        private readonly userService: UserService,
        private readonly googleIdentity: GoogleIdentityService,
        private readonly inactivity: InactivityTimeoutService,
        private readonly router: Router,
        private readonly route: ActivatedRoute
    ) {
        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    }

    ngOnInit(): void {
        // Do not auto-redirect when a session cookie exists — always show the public home first;
        // user goes to the app after explicit sign-in or by opening /dashboard (AuthGuard).
        this.loadTrending();
    }

    ngAfterViewInit(): void {
        this.googleIdentity.init();
        this.googleIdentity.setCredentialHandler((t) => this.onGoogleCredential(t));
        setTimeout(() => this.renderHeroGoogle());
    }

    ngOnDestroy(): void {
        this.googleIdentity.setCredentialHandler(() => {});
    }

    onLogoError(): void {
        this.logoLoadFailed = true;
    }

    openCreateModal(): void {
        this.authModalTab = 'create';
        this.resetCreateForm();
        this.showAuthModal = true;
        this.googleUsernameStep = false;
        setTimeout(() => this.renderModalGoogle());
    }

    openSignInModal(): void {
        this.authModalTab = 'signIn';
        this.showAuthModal = true;
        this.googleUsernameStep = false;
        setTimeout(() => this.renderModalGoogle());
    }

    closeAuthModal(): void {
        this.showAuthModal = false;
        this.googleUsernameStep = false;
        setTimeout(() => this.renderHeroGoogle());
    }

    switchModalTab(tab: 'signIn' | 'create'): void {
        this.authModalTab = tab;
        this.googleUsernameStep = false;
        setTimeout(() => this.renderModalGoogle());
    }

    private renderHeroGoogle(): void {
        if (!this.googleBtnHero?.nativeElement || this.showAuthModal) {
            return;
        }
        this.googleIdentity.renderButton(this.googleBtnHero.nativeElement);
    }

    private renderModalGoogle(): void {
        if (!this.showAuthModal || !this.googleBtnModal?.nativeElement || this.googleUsernameStep) {
            return;
        }
        this.googleIdentity.renderButton(this.googleBtnModal.nativeElement);
    }

    private loadTrending(): void {
        this.trendingLoading = true;
        this.trendingError = null;
        const path = URLs.PostsDataUrls.GetTrendingHomePosts.replace(/^\//, '');
        this.http
            .get<TrendingHomePostVM[]>(path)
            .pipe(
                timeout(12000),
                catchError(() => {
                    this.trendingError =
                        'Could not load trending posts. If this persists, the API cannot reach SQL Server (check connection string and firewall).';
                    return of([]);
                }),
                finalize(() => (this.trendingLoading = false))
            )
            .subscribe({
                next: (rows) => {
                    this.trending = (rows || []).map((r: any, i: number) => ({
                        userID: r.userID ?? r.UserID,
                        userName: r.userName ?? r.UserName,
                        profileName: r.profileName ?? r.ProfileName,
                        userImg: r.userImg ?? r.UserImg,
                        postContent: r.postContent ?? r.PostContent ?? '',
                        likes: r.likes ?? r.Likes ?? 0,
                        reposts: r.reposts ?? r.Reposts ?? 0,
                        comments: r.comments ?? r.Comments ?? 0,
                        categoryTag: (r.categoryTag ?? r.CategoryTag) || undefined,
                        animationDelayMs: 100 + i * 80
                    }));
                }
            });
    }

    login(): void {
        this.errorMessage = '';
        this.loginBusy = true;
        this.userService.login(this.email, this.password).subscribe({
            next: () => {
                this.loginBusy = false;
                this.inactivity.initializeTimer();
                this.router.navigateByUrl(this.returnUrl);
            },
            error: () => {
                this.loginBusy = false;
                this.errorMessage = 'The username or password entered is not correct.';
            }
        });
    }

    loginFromModal(): void {
        this.errorMessage = '';
        this.loginBusy = true;
        this.userService.login(this.email, this.password).subscribe({
            next: () => {
                this.loginBusy = false;
                this.inactivity.initializeTimer();
                this.closeAuthModal();
                this.router.navigateByUrl(this.returnUrl);
            },
            error: () => {
                this.loginBusy = false;
                this.errorMessage = 'The username or password entered is not correct.';
            }
        });
    }

    /** After Google username step, re-render the GIS button in the modal. */
    exitGoogleUsernameStep(): void {
        this.googleUsernameStep = false;
        setTimeout(() => this.renderModalGoogle());
    }

    onGoogleCredential(idToken: string): void {
        this.googleBusy = true;
        this.errorMessage = '';
        this.userService.googleLogin({ idToken }).subscribe({
            next: (res: any) => {
                this.googleBusy = false;
                const needsUsername = res?.needsUsername ?? res?.NeedsUsername;
                if (needsUsername) {
                    this.pendingGoogleToken = idToken;
                    this.googlePrefillEmail = res.email || res.Email || '';
                    this.googleSuggestProfile = res.suggestedProfileName || res.SuggestedProfileName || '';
                    this.googleSuggestUsername = res.suggestedUsername || res.SuggestedUsername || '';
                    this.googleUsernameStep = true;
                    this.createUsername = this.googleSuggestUsername || '';
                    return;
                }
                this.finishAuthSuccess();
            },
            error: (err) => {
                this.googleBusy = false;
                const msg = err?.error?.message || err?.message || 'Google sign-in failed.';
                if (this.showAuthModal) {
                    this.createErrors = { google: msg };
                } else {
                    this.errorMessage = msg;
                }
            }
        });
    }

    submitGoogleUsername(): void {
        const u = (this.createUsername || '').trim();
        this.validateCreateField('username', u);
        if (this.createErrors['username'] || !this.pendingGoogleToken) {
            return;
        }
        this.googleBusy = true;
        this.userService.googleLogin({ idToken: this.pendingGoogleToken, username: u }).subscribe({
            next: (res: any) => {
                this.googleBusy = false;
                const needsUsername = res?.needsUsername ?? res?.NeedsUsername;
                if (needsUsername) {
                    return;
                }
                this.googleUsernameStep = false;
                this.closeAuthModal();
                this.finishAuthSuccess();
            },
            error: (err) => {
                this.googleBusy = false;
                const msg = err?.error?.message || 'Could not complete sign-up.';
                this.createErrors = { username: msg };
            }
        });
    }

    private finishAuthSuccess(): void {
        this.inactivity.initializeTimer();
        this.closeAuthModal();
        this.router.navigateByUrl(this.returnUrl);
    }

    validateCreateField(field: string, value: string): void {
        const e = { ...this.createErrors };
        if (field === 'email') {
            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                e['email'] = 'Invalid email address';
            } else {
                delete e['email'];
            }
        }
        if (field === 'username') {
            if (value && value.length < 3) {
                e['username'] = 'Username must be at least 3 characters';
            } else if (value && !/^[a-zA-Z0-9_]+$/.test(value)) {
                e['username'] = 'Only letters, numbers, and underscores';
            } else {
                delete e['username'];
            }
        }
        if (field === 'password') {
            if (value && value.length < 8) {
                e['password'] = 'Password must be at least 8 characters';
            } else {
                delete e['password'];
            }
        }
        this.createErrors = e;
    }

    submitCreateAccount(event: Event): void {
        event.preventDefault();
        this.validateCreateField('email', this.createEmail);
        this.validateCreateField('username', this.createUsername);
        this.validateCreateField('password', this.createPassword);
        if (Object.keys(this.createErrors).length) {
            return;
        }

        const body = {
            UserInfo: {
                Name: this.createDisplayName || this.createUsername,
                ProfileBio: '',
                Birthday: '2000-01-01T00:00:00.000Z',
                Gender: '',
                RegistrationDate: new Date().toISOString(),
                IPAddress: '',
                TrustLevel: 1,
                UserImg: '',
                EntityType: 1
            },
            LoginInfo: {
                Username: this.createUsername,
                Password: this.createPassword,
                Email: this.createEmail,
                Phone: '',
                VerificationLevel: 1,
                PasswordHash: ''
            }
        };

        this.createBusy = true;
        this.userService.registerUser(body).subscribe({
            next: (ok) => {
                this.createBusy = false;
                if (ok) {
                    this.userService.login(this.createUsername, this.createPassword).subscribe({
                        next: () => {
                            this.finishAuthSuccess();
                        },
                        error: () => {
                            this.createErrors = { form: 'Account created but sign-in failed. Try Log In.' };
                        }
                    });
                } else {
                    this.createErrors = { form: 'Registration failed. Try a different username or email.' };
                }
            },
            error: () => {
                this.createBusy = false;
                this.createErrors = { form: 'Registration failed. Try a different username or email.' };
            }
        });
    }

    private resetCreateForm(): void {
        this.createEmail = '';
        this.createUsername = '';
        this.createDisplayName = '';
        this.createPassword = '';
        this.createErrors = {};
        this.createShowPassword = false;
    }
}
